import React from 'react';

import { Chip, MenuItem } from 'components';

import { dayjs, useTranslation } from 'third-party';

import { DISPLAY_DATE_FORMAT, PaymentColors } from 'constants/common';

import { ListFilter } from 'components/Table/Filtering/ListFilter';
import { ColumnFilteringProps, TableColumn } from 'components/Table/types';

import {
  formatDateTimeWithTimezone,
  humanizePascalCase,
  moneyFormatter,
} from 'utils/app';

import { APIPayment, APIPaymentStatus } from 'types/api';

export const StatusMenu = [
  <MenuItem key={APIPaymentStatus.Draft} value={APIPaymentStatus.Draft}>
    Draft
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Requested} value={APIPaymentStatus.Requested}>
    Requested
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Created} value={APIPaymentStatus.Created}>
    Created
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Void} value={APIPaymentStatus.Void}>
    Void
  </MenuItem>,
  <MenuItem
    key={APIPaymentStatus.TryToPrint}
    value={APIPaymentStatus.TryToPrint}
  >
    Try To Print
  </MenuItem>,
  <MenuItem
    key={APIPaymentStatus.OpenToPrint}
    value={APIPaymentStatus.OpenToPrint}
  >
    Open To Print
  </MenuItem>,
  <MenuItem
    key={APIPaymentStatus.CouldNotPrint}
    value={APIPaymentStatus.CouldNotPrint}
  >
    Could Not Print
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Printed} value={APIPaymentStatus.Printed}>
    Printed
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Emailed} value={APIPaymentStatus.Emailed}>
    Emailed
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Delivered} value={APIPaymentStatus.Delivered}>
    Delivered
  </MenuItem>,
  <MenuItem
    key={APIPaymentStatus.NotDelivered}
    value={APIPaymentStatus.NotDelivered}
  >
    Not Delivered
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Cleared} value={APIPaymentStatus.Cleared}>
    Cleared
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Scheduled} value={APIPaymentStatus.Scheduled}>
    Scheduled
  </MenuItem>,
];

export const PaymentStatusFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      {StatusMenu}
    </ListFilter>
  );
};

export const paymentColumns = (
  timezoneValue: string,
): TableColumn<APIPayment>[] => [
  {
    dataId: 'id',
    label: 'paymentList.column.id',
    minWidth: 80,
    width: 140,
    hidden: true,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'status',
    label: 'paymentList.column.status',
    minWidth: 80,
    width: 140,
    hidden: false,
    sortable: true,
    resizable: true,
    customFilter: PaymentStatusFilter,
    formatter: value => ({
      value: value,
      element: (
        <Chip
          label={humanizePascalCase(value)}
          style={{
            backgroundColor: PaymentColors[value as APIPaymentStatus],
          }}
        />
      ),
    }),
  },
  {
    dataId: 'bankName',
    label: 'paymentList.column.bank',
    minWidth: 80,
    width: 180,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (_, item: APIPayment) => ({
      value: item.bank?.bankName ?? ' ',
    }),
  },
  {
    dataId: 'amount',
    label: 'paymentList.column.amount',
    minWidth: 80,
    width: 120,
    hidden: false,
    sortable: true,
    formatter: value => ({ value: moneyFormatter(value as number) }),
    resizable: true,
    filterType: 'numeric',
  },
  {
    dataId: 'checkNumber',
    label: 'paymentList.column.checkNumber',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'checkDate',
    label: 'paymentList.column.checkDate',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    formatter: value => ({ value: dayjs(value).format(DISPLAY_DATE_FORMAT) }),
    resizable: true,
    filterType: 'date',
  },
  {
    dataId: 'note',
    label: 'paymentList.column.note',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'memo',
    label: 'paymentList.column.memo',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'name',
    label: 'paymentList.column.name',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'company',
    label: 'paymentList.column.company',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'nameOnCheck',
    label: 'paymentList.column.nameOnCheck',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (_, item: APIPayment) => ({
      value: item.payee?.nameOnCheck ?? ' ',
    }),
  },
  {
    dataId: 'referenceId',
    label: 'paymentList.column.referenceId',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'verifyCode',
    label: 'paymentList.column.verifyCode',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'email',
    label: 'paymentList.column.email',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (_, item: APIPayment) => ({
      value: item.payee?.email ?? ' ',
    }),
  },
  {
    dataId: 'phone',
    label: 'paymentList.column.phone',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (_, item: APIPayment) => ({
      value: item.payee?.phone ?? ' ',
    }),
  },
  {
    dataId: 'address',
    label: 'paymentList.column.address',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (_, item: APIPayment) => ({
      value: item.payee?.address ?? ' ',
    }),
  },
  {
    dataId: 'city',
    label: 'paymentList.column.city',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (_, item: APIPayment) => ({
      value: item.payee?.city ?? ' ',
    }),
  },
  {
    dataId: 'state',
    label: 'paymentList.column.state',
    minWidth: 80,
    width: 80,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (_, item: APIPayment) => ({
      value: item.payee?.state ?? '',
    }),
  },
  {
    dataId: 'zip',
    label: 'paymentList.column.zip',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (_, item: APIPayment) => ({
      value: item.payee?.zip ?? '',
    }),
  },
  {
    dataId: 'kId',
    label: 'paymentList.column.kId',
    minWidth: 80,
    width: 150,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'deliveryTime',
    label: 'paymentList.column.deliveryTime',
    minWidth: 80,
    width: 150,
    hidden: true,
    sortable: true,
    resizable: true,
    filterType: 'date',
    formatter: (_, item: APIPayment) => ({
      value: formatDateTimeWithTimezone(item?.deliveryTime, timezoneValue),
    }),
  },
  {
    dataId: 'createdOn',
    label: 'paymentList.column.createdOn',
    minWidth: 80,
    width: 180,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'date',
    formatter: (_, item: APIPayment) => ({
      value: formatDateTimeWithTimezone(item?.createdOn, timezoneValue),
    }),
  },
  {
    dataId: 'updatedOn',
    label: 'paymentList.column.updatedOn',
    minWidth: 80,
    width: 180,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'date',
    formatter: (_, item: APIPayment) => ({
      value: formatDateTimeWithTimezone(item?.updatedOn, timezoneValue),
    }),
  },
];
